import axios, { AxiosResponse } from 'axios';
import baseAPI from '@/api/base';
import { BROWSE_DRIVE_URL, DRIVE_FILES_URL, DRIVE_FOLDERS_URL, DRIVE_UPLOADS_URL, GET_DRIVE_TREE_URL, GET_DRIVE_USAGE_URL } from '@/utils/urls';
import { BrowseDriveParams, CreateDriveFolder, DriveUploadTicket, InitDriveUpload, UpdateDriveFile, UpdateDriveFolder } from '@/dtos/drive.dto';

class Drive {
  static getTree = async (): Promise<AxiosResponse> => baseAPI.get(GET_DRIVE_TREE_URL);

  static browse = async (params: BrowseDriveParams): Promise<AxiosResponse> => baseAPI.get(BROWSE_DRIVE_URL, { params });

  static getUsage = async (): Promise<AxiosResponse> => baseAPI.get(GET_DRIVE_USAGE_URL, { silent: true });

  static createFolder = async (payload: CreateDriveFolder): Promise<AxiosResponse> => baseAPI.post(DRIVE_FOLDERS_URL, payload);

  static updateFolder = async (payload: UpdateDriveFolder): Promise<AxiosResponse> => {
    const { id, ...body } = payload;
    return baseAPI.patch(`${DRIVE_FOLDERS_URL}/${id}`, body);
  };

  static deleteFolder = async (id: string): Promise<AxiosResponse> => baseAPI.delete(`${DRIVE_FOLDERS_URL}/${id}`);

  static initUpload = async (payload: InitDriveUpload): Promise<AxiosResponse> => baseAPI.post(DRIVE_UPLOADS_URL, payload);

  static completeUpload = async (id: string): Promise<AxiosResponse> => baseAPI.post(`${DRIVE_UPLOADS_URL}/${id}/complete`, {});

  /** An upload the person cancelled or that failed; best effort, never a toast. */
  static cancelUpload = async (id: string): Promise<AxiosResponse> => baseAPI.delete(`${DRIVE_UPLOADS_URL}/${id}`, { silent: true });

  static updateFile = async (payload: UpdateDriveFile): Promise<AxiosResponse> => {
    const { id, ...body } = payload;
    return baseAPI.patch(`${DRIVE_FILES_URL}/${id}`, body);
  };

  static deleteFile = async (id: string): Promise<AxiosResponse> => baseAPI.delete(`${DRIVE_FILES_URL}/${id}`);

  static downloadLink = async (id: string, inline: boolean): Promise<AxiosResponse> =>
    baseAPI.get(`${DRIVE_FILES_URL}/${id}/download`, { params: { inline: inline ? '1' : '0' } });

  /**
   * Uploads straight to the storage bucket with the presigned ticket. A plain axios call
   * on purpose: the API client would add the session header, which storage rejects.
   */
  static uploadToStorage = async (
    ticket: DriveUploadTicket,
    file: File,
    onProgress: (fraction: number) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    await axios.put(ticket.uploadUrl, file, {
      headers: ticket.headers,
      timeout: 0,
      signal,
      onUploadProgress: (event) => {
        const total = event.total ?? file.size;
        if (total > 0) onProgress(Math.min(event.loaded / total, 1));
      },
    });
  };
}

export default Drive;
